import { Injectable } from '@angular/core';
import { HttpClient } from "@angular/common/http";
import { environment } from "../../environments/environment";
import { Amplify } from "aws-amplify";
import { getAllMock } from "@mock/getAllMock";
import {
  ApiRequestError,
  CalculatePremiumRequest,
  CalculatePremiumResponse,
  CancelInsuranceRequest,
  ContractLinkRequestBody,
  ContractLinkRequestResponse,
  ContractLinkVerifyResponse,
  FnolRequest,
  FnolSubmissionResponse,
  GoogleLinkStartResponse,
  GoogleUnlinkResponse,
  InvoicePdfDto,
  NotificationDto,
  Pet,
  PetBreedDTO,
  PetInsuranceData,
  UpdatePolicyRequest,
  UserDto,
  VetChatSummaryDto
} from "../models";
import { firstValueFrom } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import { fetchAuthSession } from "aws-amplify/auth";
import { AuthLocaleService } from "./auth-locale.service";
import { get, post } from "aws-amplify/api";

@Injectable({
  providedIn: 'root'
})
export class ApiHttpService {
  BASE_URL = environment.apiBaseUrl;
  PET_API_URL = environment.petApiUrl;

  constructor(
    private httpClient: HttpClient,
    private translateService: TranslateService,
    private authLocale: AuthLocaleService,
  ) {

    const amplifyConfig = Amplify.getConfig();

    // Maybe better to move this somewhere else, but here it's work
    Amplify.configure({
      ...amplifyConfig,
      API: {
        ...amplifyConfig.API,
        REST: {
          ...amplifyConfig.API?.REST,
          'download-file': {
            endpoint: `${this.BASE_URL}/download-file`,
            region: 'eu-central-1'
          },
          'update-policy': {
            endpoint: `${this.BASE_URL}/update-policy`,
            region: 'eu-central-1'
          },
          'get-user-notifications': {
            endpoint: `${this.BASE_URL}/get-user-notifications`,
            region: 'eu-central-1'
          },
          'mark-notification-read': {
            endpoint: `${this.BASE_URL}/mark-notification-read`,
            region: 'eu-central-1'
          },
          'upload-file': {
            endpoint: `${this.BASE_URL}/upload-file`,
            region: 'eu-central-1'
          },
          'save-pet-picture': {
            endpoint: `${this.BASE_URL}/save-pet-picture`,
            region: 'eu-central-1'
          },
          'get-pet-picture': {
            endpoint: `${this.BASE_URL}/get-pet-picture`,
            region: 'eu-central-1'
          },
          'get-profile-picture': {
            endpoint: `${this.BASE_URL}/get-profile-picture`,
            region: 'eu-central-1'
          },
          'get-current-user': {
            endpoint: `${this.BASE_URL}/get-current-user`,
            region: 'eu-central-1'
          },
          'save-user': {
            endpoint: `${this.BASE_URL}/save-user`,
            region: 'eu-central-1'
          },
          'auth-test': {
            endpoint: `${this.BASE_URL}/auth-test`,
            region: 'eu-central-1'
          },
          'contact-us': {
            endpoint: `${this.BASE_URL}/contact-us`,
            region: 'eu-central-1'
          },
          'fnol': {
            endpoint: `${this.BASE_URL}/fnol`,
            region: 'eu-central-1'
          },
          'get-pet-insurance': {
            endpoint: `${this.BASE_URL}/get-pet-insurance`,
            region: 'eu-central-1'
          },
          'get-invoice-pdf': {
            endpoint: `${this.BASE_URL}/get-invoice-pdf`,
            region: 'eu-central-1'
          },
          'get-active-fnol-requests': {
            endpoint: `${this.BASE_URL}/get-active-fnol-requests`,
            region: 'eu-central-1'
          },
          'get-vet-chat-summaries': {
            endpoint: `${this.BASE_URL}/get-vet-chat-summaries`,
            region: 'eu-central-1'
          },
          'cancel-insurance': {
            endpoint: `${this.BASE_URL}/cancel-insurance`,
            region: 'eu-central-1'
          },
          'create-closing-session': {
            endpoint: `${this.BASE_URL}/create-closing-session`,
            region: 'eu-central-1'
          },
          'grant-meiko-voucher': {
            endpoint: `${this.BASE_URL}/grant-meiko-voucher`,
            region: 'eu-central-1'
          },
          'link-google-start': {
            endpoint: `${this.BASE_URL}/link/google/start`,
            region: 'eu-central-1'
          },
          'link-google-unlink': {
            endpoint: `${this.BASE_URL}/link/google/unlink`,
            region: 'eu-central-1'
          }
        }
      }
    })
  }

  getBreed(specie: 'cat' | 'dog'): Promise<PetBreedDTO[]> {
    const lang = this.translateService.getCurrentLang();
    if (specie === 'cat') {
      return firstValueFrom(this.httpClient.get<PetBreedDTO[]>(`${this.PET_API_URL}/get-cat-breeds?lang=${lang}`));
    } else {
      return firstValueFrom(this.httpClient.get<PetBreedDTO[]>(`${this.PET_API_URL}/get-dog-breeds?lang=${lang}`));
    }
  }

  async forceRefresh() {
    const token = await fetchAuthSession({ forceRefresh: true });
  }

  async calculatePremium(calculatePremium: CalculatePremiumRequest): Promise<CalculatePremiumResponse> {
    return await firstValueFrom(this.httpClient.post<CalculatePremiumResponse>(`${this.PET_API_URL}/calculate-premium-v2`, calculatePremium));
  }

  async updatePolicy(updatePolicyRequest: UpdatePolicyRequest) {
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString() || ''
    let bodyString = JSON.stringify({
      ...updatePolicyRequest,
    });

    let res;
    try {
      res = (await post({
        apiName: 'update-policy',
        path: '',
        options: {
          headers: {
            Authorization: token
          },
          body: bodyString
        },
      }).response)
    } catch (e: any) {
      console.log(e)
    }

    return res?.statusCode === 200;
  }

  async getUserNotifications() {
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString() || ''

    let res;
    try {
      res = (await get({
        apiName: 'get-user-notifications',
        path: '',
        options: {
          headers: {
            Authorization: token
          },
        },
      }).response).body.json()
    } catch (e: any) {
      console.log(e)
    }

    return res as unknown as NotificationDto[]
  }

  async markNotificationAsRead(notificationId: string) {
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString() || ''

    let res;
    try {
      res = (await post({
        apiName: 'mark-notification-read',
        path: '',
        options: {
          headers: {
            Authorization: token
          },
          body: {
            notificationId: notificationId
          }
        },
      }).response).body.json()
    } catch (e: any) {
      console.log(e)
    }

    return res as unknown as NotificationDto
  }

  async grantMeikoVoucher(): Promise<boolean> {
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString() || ''

    let res;
    try {
      res = await post({
        apiName: 'grant-meiko-voucher',
        path: '',
        options: {
          headers: {
            Authorization: token
          },
        },
      }).response;
    } catch (e: any) {
      console.log(e)
    }

    return (res as any)?.statusCode === 200;
  }

  /**
   * Asks the backend for the Google consent URL that links Google to the
   * account the Authorization header belongs to.
   *
   * Errors are intentionally not swallowed: the caller has to tell the user the
   * redirect never happened instead of leaving the button spinning.
   */
  async startGoogleLink(): Promise<GoogleLinkStartResponse> {
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString() || ''

    const res = (await get({
      apiName: 'link-google-start',
      path: '',
      options: {
        headers: {
          Authorization: token
        },
      },
    }).response).body.json();

    return res as unknown as GoogleLinkStartResponse;
  }

  /**
   * Detaches Google from the account the Authorization header belongs to.
   *
   * Answers `null` when the call did not succeed. On success the body names the
   * providers that are left, and says whether the token used was already stale
   * - see `GoogleUnlinkResponse`.
   */
  async unlinkGoogle(): Promise<GoogleUnlinkResponse | null> {
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString() || ''

    let res;
    try {
      res = await post({
        apiName: 'link-google-unlink',
        path: '',
        options: {
          headers: {
            Authorization: token
          },
        },
      }).response;
    } catch (e: any) {
      console.log(e)
    }

    if (res?.statusCode !== 200) {
      return null;
    }

    try {
      return (await res.body.json()) as unknown as GoogleUnlinkResponse;
    } catch (e: any) {
      // The unlink itself went through; an unreadable body only costs us the
      // `staleToken` hint, and the caller refreshes the token either way.
      console.log(e)
      return { linkedProviders: [] };
    }
  }

  /**
   * Asks for a confirmation code on the address the customer says holds their
   * policy.
   *
   * Unauthenticated on purpose: it is called right after a refused sign-in, so
   * there is no token to send. The answer is deliberately the same for an
   * address we know and one we have never seen, so nothing about it may reach
   * the screen.
   *
   * `lang` is the same locale the sign-up and password-reset calls send to
   * Cognito as `clientMetadata.locale`, and it decides the language of the
   * confirmation email. The backend normalises it and falls back to German, so
   * an unexpected value cannot break the call.
   */
  async requestContractLinkCode(email: string): Promise<ContractLinkRequestResponse> {
    return await firstValueFrom(
      this.httpClient.post<ContractLinkRequestResponse>(`${this.BASE_URL}/link/contract/request`, {
        email,
        lang: this.authLocale.locale,
      } satisfies ContractLinkRequestBody)
    );
  }

  /**
   * Hands the code back and receives the Google consent URL to send the browser
   * to.
   *
   * Errors are not swallowed: the code step tells a wrong code (400) apart from
   * too many attempts (429), and both from a failure it cannot name.
   */
  async verifyContractLinkCode(email: string, code: string): Promise<ContractLinkVerifyResponse> {
    return await firstValueFrom(
      this.httpClient.post<ContractLinkVerifyResponse>(`${this.BASE_URL}/link/contract/verify`, { email, code })
    );
  }

  async getSignedUrlToUploadFile() {
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString() || ''

    let res;
    try {
      res = (await get({
        apiName: 'upload-file',
        path: '',
        options: {
          headers: {
            Authorization: token
          },
        },
      }).response).body.json()
    } catch (e: any) {
      console.log(e)
    }

    return res as unknown as { location: string, filename: string };
  }

  async getUrlForPetPicture(baseNumber: string) {
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString() || ''

    let res;
    try {
      res = (await get({
        apiName: 'get-pet-picture',
        path: `/${baseNumber}`,
        options: {
          headers: {
            Authorization: token
          },
        },
      }).response).body.json()
    } catch (e: any) {
      console.log(e)
    }

    return res as unknown as { Location: string };
  }

  async getUrlForProfilePicture() {
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString() || ''

    let res;
    try {
      res = (await get({
        apiName: 'get-profile-picture',
        path: ``,
        options: {
          headers: {
            Authorization: token
          },
        },
      }).response).body.json()
    } catch (e: any) {
      console.log(e)
    }

    return res as unknown as { Location: string };
  }

  async savePetPicture(petDto: Pet) {
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString() || ''

    let res;
    try {
      res = (await post({
        apiName: 'save-pet-picture',
        path: '',
        options: {
          headers: {
            Authorization: token
          },
          body: petDto as any
        },
      }).response).body.json()
    } catch (e: any) {
      console.log(e)
    }

    return res as unknown as string;
  }

  /**
   * Reads the profile. The personal data lives in CloudInsurance now, so this
   * call reaches an external system and can answer 502. The error is raised
   * instead of swallowed, because there is no local copy to fall back to.
   */
  async getCurrentUser(): Promise<UserDto> {
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString() || ''

    try {
      // Awaited here on purpose: a rejected body promise returned from inside
      // a try block would not reach the catch.
      const res = await (await get({
        apiName: 'get-current-user',
        path: '',
        options: {
          headers: {
            Authorization: token
          },
        },
      }).response).body.json()

      return res as unknown as UserDto;
    } catch (e: any) {
      throw await this.toApiRequestError('get-current-user', e);
    }
  }

  /**
   * Writes the profile. CloudInsurance accepts a part of the fields only; for
   * the rest the backend mails customer service. A 502 means nothing was
   * saved, so the caller must keep the form as the user typed it.
   *
   * `email` is never sent. The backend reads it from the Cognito token.
   */
  async saveUser(user: Partial<UserDto>): Promise<UserDto> {
    const session = await fetchAuthSession({ forceRefresh: true })
    const token = session.tokens?.idToken?.toString() || ''

    try {
      const res = await (await post({
        apiName: 'save-user',
        path: '',
        options: {
          headers: {
            Authorization: token
          },
          body: {
            avatarImgFilename: user.avatarImgFilename,
            phoneNumber: user.phoneNumber,
            city: user.city,
            country: user.country,
            street: user.street,
            houseNumber: user.houseNumber,
            zip: user.zip,
            parent_birthdate: user.parent_birthdate,
            iban: user.iban,
            firstName: user.firstName,
            lastName: user.lastName,
          } as any
        },
      }).response).body.json()

      return res as unknown as UserDto;
    } catch (e: any) {
      throw await this.toApiRequestError('save-user', e);
    }
  }

  /**
   * Turns an Amplify REST failure into an `ApiRequestError`. The body of a 502
   * is plain text, so it is read as text and not parsed as JSON.
   */
  private async toApiRequestError(endpoint: string, e: any): Promise<ApiRequestError> {
    if (e instanceof ApiRequestError) {
      return e;
    }

    const status = e?.response?.statusCode
      ?? e?.response?.status
      ?? e?.$metadata?.httpStatusCode
      ?? e?.statusCode
      ?? e?.status;

    let bodyText = '';
    const body = e?.response?.body;

    if (typeof body === 'string') {
      bodyText = body;
    } else if (body && typeof body.text === 'function') {
      try {
        bodyText = await body.text();
      } catch {
        bodyText = '';
      }
    }

    return new ApiRequestError(
      endpoint,
      typeof status === 'number' ? status : undefined,
      bodyText || e?.message || '',
      e,
    );
  }

  async contactUs(contactUs: {
    reason: string,
    text: string
  }) {
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString() || ''

    let res;
    try {
      res = (await post({
        apiName: 'contact-us',
        path: '',
        options: {
          headers: {
            Authorization: token
          },
          body: {
            reason: contactUs.reason,
            text: contactUs.text
          } as any
        }
      }).response).body
    } catch (e: any) {
      console.log(e)
    }

    return res as unknown;
  }

  /**
   * Sends the claim. Errors are raised instead of swallowed: on a failure the
   * caller must tell the user and keep the form, not show the success dialog.
   *
   * Amplify retries a 5xx or a network error and makes 3 attempts in total, so
   * a timeout reaches the caller only after all attempts failed. The retries
   * are safe: the backend finds the saved submission and makes no duplicate.
   */
  async sendFnolRequest(body: any): Promise<FnolSubmissionResponse | undefined> {
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString() || ''

    let res;
    try {
      res = (await post({
        apiName: 'fnol',
        path: '',
        options: {
          headers: {
            Authorization: token
          },
          body: body as any
        }
      }).response).body
    } catch (e: any) {
      throw await this.toApiRequestError('fnol', e);
    }

    // The backend saved the claim at this point, so a body we cannot read is
    // still a success and must not show the failure dialog.
    try {
      return await res.json() as unknown as FnolSubmissionResponse;
    } catch {
      return undefined;
    }
  }

  async getActiveFnolRequests() {
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString() || ''
    let res;
    try {
      res = (await get({
        apiName: 'get-active-fnol-requests',
        path: '',
        options: {
          headers: {
            Authorization: token
          },
        },
      }).response).body.json()
    } catch (e: any) {
      console.log(e)
    }

    return res as unknown as FnolRequest[];
  }

  async getPetInsurance() {
    if (!environment.production && environment.mockPetInsurance) {
      return getAllMock as unknown as PetInsuranceData[];
    }

    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString() || ''
    let res;
    try {
      res = (await get({
        apiName: 'get-pet-insurance',
        path: '',
        options: {
          headers: {
            Authorization: token
          }
        }
      }).response).body.json()
    } catch (e: any) {
      console.log(e)
    }

    return res as unknown as PetInsuranceData[];
  }

  /**
   * Fetches a single invoice PDF. The contract list only carries the invoice's
   * id and a flag, so the document is pulled on demand.
   *
   * Errors are intentionally not swallowed here: the caller turns the HTTP
   * status into the message the customer sees.
   */
  async getInvoicePdf(invoiceId: string) {
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString() || ''

    const res = (await get({
      apiName: 'get-invoice-pdf',
      path: `/${encodeURIComponent(invoiceId)}`,
      options: {
        headers: {
          Authorization: token
        }
      }
    }).response).body.json();

    return res as unknown as InvoicePdfDto;
  }

  async authTest() {
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString() || ''

    let res;
    try {
      res = (await get({
        apiName: 'auth-test',
        path: '',
        options: {
          headers: {
            Authorization: token
          },
        },
      }).response).body.json()
    } catch (e: any) {
      console.log(e)
    }

    return res as unknown as FnolRequest[];
  }

  async getFileDownloadUrl(fnolRequestId: string, s3Id: string) {
    // `${this.BASE_URL}/download-file/${fnolRequestId}/${s3Id}`;
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString() || ''

    // Errors are intentionally not swallowed here: the caller surfaces the
    // failure (including the HTTP status) to the user.
    const res = (await get({
      apiName: 'download-file',
      path: `/${fnolRequestId}/${s3Id}`,
      options: {
        headers: {
          Authorization: token
        }
      },
    }).response).body.json();

    return res as any;
  }

  /*  async calculatePaymentAPI(fnolRequest: FnolRequest) {
      const session = await fetchAuthSession()
      const token = session.tokens?.idToken?.toString() || ''

      let res;
      try {
        res = (await post({
          apiName: 'calculate-payment',
          path: '',
          options: {
            headers: {
              Authorization: token
            },
            body: JSON.stringify({
              fnolRequest,
            })
          },
        }).response).body.json()
      } catch (e: any) {
        this.showSnackBarError(e.message || 'Unknown error');
      }

      return res as unknown as MfvClaim[]
    }*/

  async getVetChatSummaries(): Promise<VetChatSummaryDto[]> {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString() || '';

    let res;
    try {
      res = (await get({
        apiName: 'get-vet-chat-summaries',
        path: '',
        options: {
          headers: {
            Authorization: token
          },
        },
      }).response).body.json();
    } catch (e: any) {
      console.log(e);
    }

    return res as unknown as VetChatSummaryDto[];
  }

  async createClosingSession(): Promise<{ sessionId: string } | null> {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString() || '';

    let res;
    try {
      res = (await post({
        apiName: 'create-closing-session',
        path: '',
        options: {
          headers: { Authorization: token },
        },
      }).response).body.json();
    } catch (e: any) {
      console.log(e);
    }

    return res as unknown as { sessionId: string } | null;
  }

  /**
   * Sends the cancellation to customer service. Rejects when it was not sent - Amplify
   * rejects every non-2xx answer - so the caller never thanks the customer for a
   * cancellation nobody received.
   */
  async cancelInsurance(cancelInsuranceRequest: CancelInsuranceRequest): Promise<void> {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString() || '';

    await post({
      apiName: 'cancel-insurance',
      path: '',
      options: {
        headers: {
          Authorization: token
        },
        body: cancelInsuranceRequest as any
      },
    }).response;
  }
}
